'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getLuckyTicketsByRaffle } from '@/actions/luckyTicketsByRaffle';
import type { LuckyTicketsByRaffle } from '@/types/models/LuckyTicketsByRaffle';
import { logger } from '@/utils';

type BreakpointType = 'default' | 'sm' | 'md' | 'lg' | 'xl';
type ItemsPerPageType = Record<BreakpointType, { found: number; notFound: number }>;

const itemsPerPage: ItemsPerPageType = {
  default: { found: 24, notFound: 24 },
  sm: { found: 24, notFound: 24 },
  md: { found: 24, notFound: 24 },
  lg: { found: 24, notFound: 24 },
  xl: { found: 24, notFound: 24 },
};

const getActiveBreakpoint = (): BreakpointType => {
  if (typeof window === 'undefined') return 'default';

  const width = window.innerWidth;
  if (width >= 1280) return 'xl';
  if (width >= 1024) return 'lg';
  if (width >= 768) return 'md';
  if (width >= 640) return 'sm';
  return 'default';
};

type UseLuckySequencesParams = {
  raffleId: string;
  foundSequences?: LuckyTicketsByRaffle;
  notFoundSequences?: LuckyTicketsByRaffle;
};

type UseLuckySequencesReturn = {
  foundSequences: LuckyTicketsByRaffle['data'];
  notFoundSequences: LuckyTicketsByRaffle['data'];
  totalFound: number;
  totalNotFound: number;
  foundPageCount: number;
  notFoundPageCount: number;
  loading: boolean;
  paginationLoading: boolean;
  error: string | null;
  handlePaginationChange: (page: number, type: 'found' | 'notFound') => void;
  noLuckySequences: boolean;
  luckySequenceSliderProps: {
    foundLuckySeq: LuckyTicketsByRaffle['data'];
    notFoundLuckySeq: LuckyTicketsByRaffle['data'];
    totalFoundLuckySeq: number;
    totalNotFoundLuckySeq: number;
    foundPageCount: number;
    notFoundPageCount: number;
    handlePaginationChange: (page: number, type: string) => void;
    showAvailable: boolean;
    isPaginating: boolean;
  };
};

export const useLuckySequences = ({
  raffleId,
  foundSequences,
  notFoundSequences,
}: UseLuckySequencesParams): UseLuckySequencesReturn => {
  const [foundData, setFoundData] = useState<LuckyTicketsByRaffle | null>(
    foundSequences || null,
  );
  const [notFoundData, setNotFoundData] = useState<LuckyTicketsByRaffle | null>(
    notFoundSequences || null,
  );
  const [loading, setLoading] = useState(!foundSequences && !notFoundSequences);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const screenSize = useMemo(() => getActiveBreakpoint(), []);

  const fetchFoundSequences = useCallback(
    async (take?: number, skip?: number) => {
      try {
        const response = await getLuckyTicketsByRaffle(raffleId, {
          found: true,
          take: take || itemsPerPage[screenSize].found,
          skip: skip || 0,
        });

        if (response) {
          setFoundData(response);
        }
      } catch (err) {
        logger.error('Error fetching found lucky sequences:', err);
        setError('Erro ao carregar sequências encontradas');
      }
    },
    [raffleId, screenSize],
  );

  const fetchNotFoundSequences = useCallback(
    async (take?: number, skip?: number) => {
      try {
        const response = await getLuckyTicketsByRaffle(raffleId, {
          found: false,
          take: take || itemsPerPage[screenSize].notFound,
          skip: skip || 0,
        });

        if (response) {
          setNotFoundData(response);
        }
      } catch (err) {
        logger.error('Error fetching not found lucky sequences:', err);
        setError('Erro ao carregar sequências não encontradas');
      }
    },
    [raffleId, screenSize],
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);

      try {
        await Promise.all([fetchFoundSequences(), fetchNotFoundSequences()]);
      } catch (err) {
        logger.error('Error fetching initial lucky sequences:', err);
        setError('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    if (!foundSequences && !notFoundSequences && raffleId) {
      fetchInitialData();
    }
  }, [
    raffleId,
    foundSequences,
    notFoundSequences,
    fetchFoundSequences,
    fetchNotFoundSequences,
  ]);

  const foundSequencesData = useMemo(() => foundData?.data || [], [foundData?.data]);
  const notFoundSequencesData = useMemo(
    () => notFoundData?.data || [],
    [notFoundData?.data],
  );
  const totalFound = foundData?.totalCount || 0;
  const totalNotFound = notFoundData?.totalCount || 0;

  const foundPageCount = useMemo(
    () => Math.ceil(totalFound / itemsPerPage[screenSize].found),
    [totalFound, screenSize],
  );

  const notFoundPageCount = useMemo(
    () => Math.ceil(totalNotFound / itemsPerPage[screenSize].notFound),
    [totalNotFound, screenSize],
  );

  const noLuckySequences = useMemo(
    () => !foundSequencesData.length && !notFoundSequencesData.length,
    [foundSequencesData.length, notFoundSequencesData.length],
  );

  const handlePaginationChange = useCallback(
    (page: number, type: 'found' | 'notFound') => {
      const take =
        type === 'found'
          ? itemsPerPage[screenSize].found
          : itemsPerPage[screenSize].notFound;
      const skip = (page - 1) * take;

      setPaginationLoading(true);
      if (type === 'found') {
        fetchFoundSequences(take, skip).finally(() => setPaginationLoading(false));
      } else {
        fetchNotFoundSequences(take, skip).finally(() => setPaginationLoading(false));
      }
    },
    [screenSize, fetchFoundSequences, fetchNotFoundSequences],
  );

  const luckySequenceSliderProps = useMemo(
    () => ({
      foundLuckySeq: foundSequencesData,
      notFoundLuckySeq: notFoundSequencesData,
      totalFoundLuckySeq: totalFound,
      totalNotFoundLuckySeq: totalNotFound,
      foundPageCount,
      notFoundPageCount,
      handlePaginationChange: (page: number, type: string) => {
        handlePaginationChange(page, type as 'found' | 'notFound');
      },
      showAvailable: true,
      isPaginating: paginationLoading,
    }),
    [
      foundSequencesData,
      notFoundSequencesData,
      totalFound,
      totalNotFound,
      foundPageCount,
      notFoundPageCount,
      handlePaginationChange,
      paginationLoading,
    ],
  );

  return {
    foundSequences: foundSequencesData,
    notFoundSequences: notFoundSequencesData,
    totalFound,
    totalNotFound,
    foundPageCount,
    notFoundPageCount,
    loading,
    paginationLoading,
    error,
    handlePaginationChange,
    noLuckySequences,
    luckySequenceSliderProps,
  };
};
